<template>
  <div class="st-page py-3">
    <header class="st-toolbar">
      <div class="st-toolbar__title">
        <span class="st-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>States</h1>
          <p>Physical states — global reference data shared across all organisations. Branches sit under a state.</p>
        </div>
      </div>
      <div class="st-toolbar__actions">
        <CButton color="primary" size="sm" @click="openCreateModal">+ Add State</CButton>
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadStates"
        >
          {{ isLoading ? 'Refreshing…' : 'Refresh' }}
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="st-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="st-alert">{{ successMessage }}</CAlert>

    <BaseTable
      title="All States"
      :items="states"
      :columns="columns"
      :loading="isLoading"
      :default-sort="{ column: 'name', direction: 'asc' }"
      striped
      hover
      bordered
      sticky-header
      empty-text="No states yet — add Kerala, Tamil Nadu, etc. to get started."
    >
      <template #cell-actions="{ row }">
        <div class="st-row-actions">
          <button type="button" class="st-action-btn st-action-btn--edit" @click="openEditModal(row)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            <span>Edit</span>
          </button>
          <button type="button" class="st-action-btn st-action-btn--delete" @click="openDeleteConfirm(row)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6"/>
              <path d="M14 11v6"/>
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
            </svg>
            <span>Delete</span>
          </button>
        </div>
      </template>
    </BaseTable>

    <CModal
      :show.sync="showModal"
      size="md"
      :centered="true"
      :title="modalTitle"
      class="st-modal"
      :close-on-backdrop="!isSaving"
    >
      <div class="st-modal__body">
        <div v-if="modalError" class="st-modal__alert">{{ modalError }}</div>
        <div class="st-grid">
          <div class="st-field st-field--wide">
            <AppInput
              no-margin
              label="State name"
              required
              :value="form.name"
              :disabled="isSaving"
              placeholder="e.g. Kerala"
              @update:value="form.name = $event"
            />
          </div>
        </div>
      </div>
      <template #footer>
        <button type="button" class="st-btn st-btn--ghost" :disabled="isSaving" @click="closeModal">Cancel</button>
        <button type="button" class="st-btn st-btn--primary" :disabled="isSaving || !isFormValid" @click="submitModal">
          {{ isSaving ? 'Saving…' : 'Save State' }}
        </button>
      </template>
    </CModal>

    <AppConfirmDialog
      :visible="showDeleteConfirm"
      title="Delete this state?"
      :message="deleteConfirmMessage"
      confirm-label="Delete"
      cancel-label="Keep"
      busy-label="Deleting…"
      :busy="isDeleting"
      variant="danger"
      :close-on-backdrop="!isDeleting"
      @confirm="confirmDelete"
      @cancel="closeDeleteConfirm"
    />
  </div>
</template>

<script>
import AppInput from '@/components/AppInput.vue'
import BaseTable from '@/components/BaseTable.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import statesApi from '@/api/states.js'

function blankForm () {
  return { id: null, name: '' }
}

export default {
  name: 'SettingsStates',
  components: { AppInput, BaseTable, AppConfirmDialog },
  data () {
    return {
      states: [],
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      showModal: false,
      isEdit: false,
      isSaving: false,
      modalError: '',
      form: blankForm(),
      showDeleteConfirm: false,
      stateToDelete: null,
      isDeleting: false,
      columns: [
        { key: 'id', label: 'Id', width: '64px', align: 'right' },
        { key: 'name', label: 'State' },
        { key: 'actions', label: 'Actions', width: '170px', align: 'right', sortable: false }
      ]
    }
  },
  computed: {
    modalTitle () {
      return this.isEdit ? 'Rename State' : 'Add State'
    },
    isFormValid () {
      return String(this.form.name || '').trim().length > 0
    },
    deleteConfirmMessage () {
      const s = this.stateToDelete
      if (!s) return ''
      return `Permanently remove ${s.name}. The server will block this if any branches still reference it.`
    }
  },
  created () {
    this.loadStates()
  },
  methods: {
    loadStates () {
      this.isLoading = true
      this.errorMessage = ''
      statesApi.list(this.$apiAdress)
        .then((data) => {
          this.states = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Unable to load states.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    openCreateModal () {
      this.form = blankForm()
      this.isEdit = false
      this.modalError = ''
      this.showModal = true
    },
    openEditModal (row) {
      this.form = { id: row.id, name: row.name }
      this.isEdit = true
      this.modalError = ''
      this.showModal = true
    },
    closeModal () {
      if (this.isSaving) return
      this.showModal = false
      this.form = blankForm()
      this.modalError = ''
    },
    submitModal () {
      if (!this.isFormValid) return
      this.isSaving = true
      this.modalError = ''
      const payload = { name: this.form.name.trim() }
      const request = this.isEdit
        ? statesApi.update(this.$apiAdress, this.form.id, payload)
        : statesApi.create(this.$apiAdress, payload)
      request
        .then(() => {
          this.successMessage = this.isEdit ? 'State updated.' : 'State created.'
          setTimeout(() => { this.successMessage = '' }, 2500)
          this.showModal = false
          this.form = blankForm()
          this.loadStates()
        })
        .catch((error) => {
          this.modalError = error?.response?.data?.message
            || this.firstValidationError(error?.response?.data?.error)
            || 'Failed to save state.'
        })
        .finally(() => {
          this.isSaving = false
        })
    },
    openDeleteConfirm (row) {
      this.stateToDelete = row
      this.showDeleteConfirm = true
    },
    closeDeleteConfirm () {
      if (this.isDeleting) return
      this.showDeleteConfirm = false
      this.stateToDelete = null
    },
    confirmDelete () {
      if (!this.stateToDelete) return
      this.isDeleting = true
      statesApi.remove(this.$apiAdress, this.stateToDelete.id)
        .then(() => {
          this.successMessage = 'State deleted.'
          setTimeout(() => { this.successMessage = '' }, 2500)
          this.showDeleteConfirm = false
          this.stateToDelete = null
          this.loadStates()
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Failed to delete state.'
          this.showDeleteConfirm = false
          this.stateToDelete = null
        })
        .finally(() => {
          this.isDeleting = false
        })
    },
    firstValidationError (errorMap) {
      if (!errorMap || typeof errorMap !== 'object') return ''
      const firstKey = Object.keys(errorMap)[0]
      const list = errorMap[firstKey]
      return Array.isArray(list) ? list[0] : ''
    }
  }
}
</script>

<style scoped>
.st-page { padding: 0.25rem 0; }
.st-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.75rem; padding: 0.25rem 0.25rem 1rem; flex-wrap: wrap;
}
.st-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }
.st-toolbar__accent {
  width: 4px; height: 36px; border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}
.st-toolbar h1 {
  margin: 0; font-size: 1.15rem; font-weight: 700;
  color: #1f2a44; letter-spacing: -0.01em; line-height: 1.2;
}
.st-toolbar p { margin: 0; font-size: 0.78rem; color: #6b7280; }
.st-toolbar__actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.st-toolbar__actions >>> .btn { border-radius: 8px; font-weight: 600; font-size: 0.82rem; }
.st-toolbar__actions >>> .btn.refresh-btn {
  height: 34px; padding: 0 0.85rem;
  background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44;
}
.st-alert { margin: 0 0 0.75rem; border-radius: 10px; }
.st-row-actions {
  display: inline-flex; align-items: center; gap: 0.4rem; justify-content: flex-end;
}
.st-action-btn {
  display: inline-flex; align-items: center; gap: 0.35rem;
  height: 28px; padding: 0 0.7rem; border-radius: 6px;
  border: 1px solid #e5e9f2; background: #ffffff; color: #1f2a44;
  font-size: 0.76rem; font-weight: 600; cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}
.st-action-btn:focus { outline: none; }
.st-action-btn:focus-visible {
  outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}
.st-action-btn--edit:hover {
  border-color: #16a07a; color: #0e5b46; background: #f5fbf8;
  box-shadow: 0 2px 4px rgba(14, 91, 70, 0.1);
}
.st-action-btn--delete:hover {
  border-color: #ef4444; color: #b91c1c; background: #fef2f2;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.12);
}
.st-action-btn--delete:focus-visible {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.30);
}
.st-modal >>> .modal-content {
  border-radius: 14px; border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22); overflow: hidden;
}
.st-modal >>> .modal-header {
  border-bottom: 1px solid #eef1f7; background: #ffffff; padding: 0.95rem 1.15rem;
}
.st-modal >>> .modal-header .modal-title {
  font-size: 1.05rem; font-weight: 700; color: #1f2a44; margin: 0; letter-spacing: -0.01em;
}
.st-modal >>> .modal-body {
  background: #fbfcfe; padding: 1.1rem;
  max-height: calc(100vh - 220px); overflow-y: auto;
}
.st-modal >>> .modal-footer {
  border-top: 1px solid #eef1f7; background: #ffffff; padding: 0.7rem 1.15rem; gap: 0.5rem;
}
.st-modal__body { display: flex; flex-direction: column; gap: 0.85rem; }
.st-modal__alert {
  padding: 0.6rem 0.85rem; background: #fee2e2; color: #991b1b;
  border: 1px solid #fecaca; border-radius: 8px;
  font-size: 0.82rem; font-weight: 600;
}
.st-grid {
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem;
}
.st-field { display: flex; flex-direction: column; min-width: 0; }
.st-field--wide { grid-column: 1 / -1; }
.st-btn {
  display: inline-flex; align-items: center; justify-content: center;
  height: 34px; min-width: 90px; padding: 0.45rem 1.1rem; border-radius: 8px;
  font-family: inherit; font-size: 0.85rem; font-weight: 600; cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.st-btn:focus { outline: none; }
.st-btn:focus-visible {
  outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}
.st-btn:disabled { cursor: not-allowed; opacity: 0.6; filter: none; transform: none; box-shadow: none; }
.st-btn--ghost {
  background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44;
}
.st-btn--ghost:hover:not(:disabled) { background: #f8fafc; border-color: #d8dde6; }
.st-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0; color: #ffffff; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}
.st-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}
@media (max-width: 575px) {
  .st-grid { grid-template-columns: 1fr; }
}
</style>
